import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Combobox,
  Option,
  Input,
  Textarea,
  Radio,
  RadioGroup,
  Field,
  makeStyles,
  tokens,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { useUiStore } from '@/stores/uiStore';
import { list365Groups, createSpace, type M365Group } from '@/services/api/admin';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
  },
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS },
});

type Mode = 'existing' | 'new';

export function SpaceCreate() {
  const styles = useStyles();
  const setView = useUiStore((s) => s.setView);
  const qc = useQueryClient();

  const [mode, setMode] = useState<Mode>('existing');
  const [name, setName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [searchGroup, setSearchGroup] = useState('');
  const [newGroup, setNewGroup] = useState({
    displayName: '',
    mailNickname: '',
    description: '',
    visibility: 'Private' as 'Private' | 'Public',
  });
  const [error, setError] = useState<string | null>(null);

  const groupsQuery = useQuery<M365Group[]>({
    queryKey: ['365-groups', searchGroup],
    queryFn: () => list365Groups(searchGroup || undefined),
    enabled: mode === 'existing',
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: createSpace,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['containers'] });
      setView('settings');
    },
    onError: (err: Error) => setError(err.message),
  });

  const canSubmit =
    name.trim().length > 0 &&
    ((mode === 'existing' && selectedGroupId) ||
      (mode === 'new' && newGroup.displayName.trim() && newGroup.mailNickname.trim()));

  const onSubmit = () => {
    setError(null);
    addBreadcrumb({ category: 'ui.click', message: 'create-space-submit', data: { mode } });
    if (mode === 'existing' && selectedGroupId) {
      createMutation.mutate({ name, m365GroupId: selectedGroupId });
    } else if (mode === 'new') {
      createMutation.mutate({
        name,
        newGroup: { ...newGroup },
      });
    }
  };

  return (
    <div className={styles.root}>
      <Text size={400} weight="semibold">
        Nouvel espace de service
      </Text>
      <Text size={200}>
        Un espace est adossé à un groupe Microsoft 365 (lien 1:1). Choisissez un groupe existant
        ou créez-en un nouveau.
      </Text>

      <Field label="Nom affiché dans l'add-in">
        <Input value={name} onChange={(_e, d) => setName(d.value)} placeholder="ex : Comptabilité" />
      </Field>

      <RadioGroup value={mode} onChange={(_e, d) => setMode(d.value as Mode)}>
        <Radio value="existing" label="Rattacher à un groupe 365 existant (recommandé)" />
        <Radio value="new" label="Créer un nouveau groupe 365" />
      </RadioGroup>

      {mode === 'existing' && (
        <Field label="Groupe Microsoft 365">
          <Combobox
            freeform
            placeholder="Taper pour filtrer…"
            value={searchGroup}
            onInput={(e) => {
              setSearchGroup((e.target as HTMLInputElement).value);
              // Typing in the field invalidates the previous selection so the
              // user can search again without first clearing it manually.
              if (selectedGroupId) setSelectedGroupId(null);
            }}
            selectedOptions={selectedGroupId ? [selectedGroupId] : []}
            onOptionSelect={(_e, data) => {
              setSelectedGroupId(data.optionValue ?? null);
              const g = (groupsQuery.data ?? []).find((x) => x.id === data.optionValue);
              setSearchGroup(g?.displayName ?? '');
            }}
          >
            {groupsQuery.isPending && <Spinner size="tiny" />}
            {(groupsQuery.data ?? []).map((g) => (
              <Option key={g.id} value={g.id} text={g.displayName ?? g.id}>
                {g.displayName ?? g.id}
                {g.mail ? ` · ${g.mail}` : ''}
              </Option>
            ))}
          </Combobox>
        </Field>
      )}

      {mode === 'new' && (
        <div className={styles.group}>
          <Field label="Nom du groupe 365">
            <Input
              value={newGroup.displayName}
              onChange={(_e, d) => setNewGroup((g) => ({ ...g, displayName: d.value }))}
            />
          </Field>
          <Field label="Alias mail (sans @)">
            <Input
              value={newGroup.mailNickname}
              onChange={(_e, d) => setNewGroup((g) => ({ ...g, mailNickname: d.value }))}
              placeholder="ex : comptabilite"
            />
          </Field>
          <Field label="Description (optionnel)">
            <Textarea
              value={newGroup.description}
              onChange={(_e, d) => setNewGroup((g) => ({ ...g, description: d.value }))}
            />
          </Field>
          <Field label="Visibilité">
            <RadioGroup
              value={newGroup.visibility}
              onChange={(_e, d) =>
                setNewGroup((g) => ({ ...g, visibility: d.value as 'Private' | 'Public' }))
              }
              layout="horizontal"
            >
              <Radio value="Private" label="Privé" />
              <Radio value="Public" label="Public" />
            </RadioGroup>
          </Field>
        </div>
      )}

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.actions}>
        <Button appearance="secondary" onClick={() => setView('settings')}>
          Annuler
        </Button>
        <Button
          appearance="primary"
          onClick={onSubmit}
          disabled={!canSubmit || createMutation.isPending}
        >
          {createMutation.isPending ? 'Création…' : "Créer l'espace"}
        </Button>
      </div>
    </div>
  );
}
